import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface ListState {
  scrollPosition?: number;
  filters?: Record<string, any>;
  searchQuery?: string;
  page?: number;
}

export const useListState = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const saveListState = useCallback((state: ListState) => {
    const key = `list_state_${location.pathname}`;
    sessionStorage.setItem(key, JSON.stringify({
      ...state,
      timestamp: Date.now(),
    }));
  }, [location.pathname]);

  const getListState = useCallback((): ListState | null => {
    const key = `list_state_${location.pathname}`;
    const saved = sessionStorage.getItem(key);
    if (!saved) return null;
    
    try {
      const parsed = JSON.parse(saved);
      if (Date.now() - parsed.timestamp > 30 * 60 * 1000) {
        sessionStorage.removeItem(key);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }, [location.pathname]);

  const navigateToDetail = useCallback((detailPath: string, itemId: string) => {
    const listState: ListState = {
      scrollPosition: window.scrollY,
      filters: (location.state as any)?.filters,
      searchQuery: (location.state as any)?.searchQuery,
    };
    saveListState(listState);
    
    navigate(detailPath, { 
      state: { from: location.pathname, itemId } 
    });
  }, [navigate, location, saveListState]);

  const restoreListState = useCallback(() => {
    const state = getListState();
    if (state?.scrollPosition) {
      setTimeout(() => {
        window.scrollTo(0, state.scrollPosition!);
      }, 100);
    }
    return state;
  }, [getListState]);

  return {
    saveListState,
    getListState,
    navigateToDetail,
    restoreListState,
  };
};
